import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";
const toDateStr = (d) => d.toISOString().split("T")[0];

const PIE_SEGMENTS = [
  { key: "succeeded", label: "Succeeded", color: "#2ecc71" },
  { key: "failedWithError", label: "Failed with Error", color: "#e67e22" },
  { key: "failed", label: "Failed", color: "#e74c3c" },
  { key: "needAttention", label: "Need Attention", color: "#f59e0b" },
];

const METRICS = [
  { key: "received", label: "Accounts Received", icon: "📩", color: "#58a6ff" },
  { key: "succeeded", label: "Processed Successfully", icon: "✅", color: "#2ecc71" },
  { key: "failedWithError", label: "Failed with Error", icon: "⚠️", color: "#e67e22" },
  { key: "failed", label: "Failed", icon: "❌", color: "#e74c3c" },
  { key: "needAttention", label: "Retried, Need Attention", icon: "🔄", color: "#f59e0b" },
];

function PieChart({ segments, total }) {
  const size = 180;
  const cx = size / 2, cy = size / 2;
  const radius = 70, strokeWidth = 28;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const arcs = segments.filter((s) => s.value > 0).map((s) => {
    const pct = total > 0 ? s.value / total : 0;
    const dashLen = pct * circumference;
    const arc = { ...s, dashLen, dashOffset: -offset };
    offset += dashLen;
    return arc;
  });

  return (
    <div className="pie-chart-wrapper">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#21262d" strokeWidth={strokeWidth} />
        {arcs.map((a) => (
          <circle
            key={a.key}
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke={a.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${a.dashLen} ${circumference - a.dashLen}`}
            strokeDashoffset={a.dashOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition: "stroke-dasharray 0.4s, stroke-dashoffset 0.4s" }}
          />
        ))}
        <text x={cx} y={cy - 8} textAnchor="middle" fill="#e0e0e0" fontSize="28" fontWeight="800">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="#8b949e" fontSize="10">TOTAL</text>
      </svg>
      <div className="pie-legend">
        {segments.map((s) => (
          <div key={s.key} className="pie-legend-item">
            <span className="pie-dot" style={{ background: s.color }} />
            <span className="pie-legend-label">{s.label}</span>
            <span className="pie-legend-value">{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsCharts({ refreshKey }) {
  const [selectedDate, setSelectedDate] = useState(toDateStr(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (date) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/charts?date=${date}`);
      setData(data);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(selectedDate); }, [selectedDate, load, refreshKey]);

  const pieSegments = data?.allTime
    ? PIE_SEGMENTS.map((s) => ({ ...s, value: data.allTime[s.key] ?? 0 }))
    : PIE_SEGMENTS.map((s) => ({ ...s, value: 0 }));

  const total = data?.totalAllTime ?? 0;
  const totalFailures = (data?.allTime?.failedWithError ?? 0) + (data?.allTime?.failed ?? 0);
  const failurePct = total > 0 ? ((totalFailures / total) * 100).toFixed(1) : "0.0";

  return (
    <div className="charts-panel">
      <div className="pie-card">
        <div className="pie-card-title">Total Messages Processed</div>
        <PieChart segments={pieSegments} total={total} />
        <div className="failure-pct">
          <span className="failure-pct-value">{failurePct}%</span>
          <span className="failure-pct-label">Human Intervene Required</span>
        </div>
      </div>

      <div className="date-picker-row">
        <label htmlFor="chart-date">Select Date:</label>
        <input
          id="chart-date"
          type="date"
          value={selectedDate}
          max={toDateStr(new Date())}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
      </div>

      {loading ? (
        <p className="info">Loading...</p>
      ) : data ? (
        <div className="metric-cards">
          {METRICS.map((m) => (
            <div key={m.key} className="metric-card" style={{ borderLeftColor: m.color }}>
              <div className="metric-icon">{m.icon}</div>
              <div className="metric-info">
                <div className="metric-value" style={{ color: m.color }}>{data[m.key] ?? 0}</div>
                <div className="metric-label">{m.label}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default StatsCharts;
